<template>
  <div class="login-page">
    <!-- Pattern images -->
    <div class="pattern-left">
      <img src="@/assets/img/left-pattern.png" alt="Left Pattern" />
    </div>
    <div class="pattern-right">
      <img src="@/assets/img/right-pattern.png" alt="Right Pattern" />
    </div>

    <div class="container">
      <div class="row justify-content-center align-items-center min-vh-100">
        <div class="col-md-5">
          <div class="card card-custom border-0 shadow-lg">
            <div class="card-body p-5">
              <div class="text-center mb-4">
                <img
                  :src="brandLogo"
                  :alt="brandName"
                  class="mb-3"
                  width="155"
                />
                <h3 class="fw-bold text-title">Sign in to your account</h3>
              </div>

              <!-- Email Input -->
              <div class="mb-3">
                <label for="email" class="form-label text-label"
                  >Email Address</label
                >
                <div class="position-relative">
                  <input
                    class="form-control pe-5 custom-input"
                    type="email"
                    v-model="email"
                    id="email"
                    placeholder="Enter your registered email"
                  />
                </div>
                <div
                  v-if="emailError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ emailError }}</span>
                </div>
              </div>

              <!-- Password Input -->
              <div class="mb-3">
                <label for="password" class="form-label text-label"
                  >Password</label
                >
                <div class="position-relative">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control pe-5 custom-input"
                    id="password"
                    v-model="password"
                    placeholder="Enter password"
                  />
                  <div
                    class="position-absolute top-50 end-0 translate-middle-y pe-3"
                    style="cursor: pointer"
                    @click="togglePasswordVisibility('password')"
                  >
                    <svg
                      v-if="!showPassword"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-eye text-secondary"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                      />
                      <path
                        d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                      />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-eye-slash text-secondary"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
                      />
                      <path
                        d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
                      />
                      <path
                        d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  v-if="passwordError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ passwordError }}</span>
                </div>
                <div
                  v-if="loginError"
                  class="d-flex align-items-center text-danger small mt-1"
                >
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  <span>{{ loginError }}</span>
                </div>
              </div>

              <div class="d-flex justify-content-end mb-4">
                <a
                  href="/forgot-password"
                  class="text-decoration-none text-success-custom forgot-password-link"
                  >Forgot Password?</a
                >
              </div>

              <argon-button
                @click="submitForm"
                class="btn btn-success-custom btn-lg w-100 custom-button"
                >Continue</argon-button
              >

              <div class="text-center mt-4">
                <p class="mb-0">
                  Don't have an account?
                  <a
                    href="/signup"
                    class="text-success-custom text-decoration-none signup-link"
                    >Create an Account</a
                  >
                </p>
              </div>
            </div>

            <!-- Footer inside card -->
            <div class="card-footer bg-white border-0 text-center py-3">
              <small class="text-muted"
                >© PT Anugrah Multi Remittance 2025 |
                <a href="#" @click.prevent="openTermsModal" class="text-decoration-none text-muted"
                  >Terms & Conditions</a
                >
                |
                <a href="#" @click.prevent="openPrivacyModal" class="text-decoration-none text-muted"
                  >Privacy Policy</a
                ></small
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <TermsAndConditionsModal v-model="showTermsModal" />

  <!-- Privacy Policy Modal -->
  <div v-if="showPrivacyModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showPrivacyModal = false">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Privacy Policy</h5>
          <button type="button" class="btn-close" @click="showPrivacyModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="fw-bold mb-3">Last Updated: July 21, 2025</p>
          
          <h6 class="fw-bold mt-4">1. Introduction</h6>
          <p>PT Anugrah Multi Remittance, hereinafter referred to as "{{ brandName }}", respects and is fully committed to protecting the privacy of our Users. This Privacy Policy explains how we collect, store, use, disclose, and protect personal data obtained through the use of {{ brandName }}' international remittance services.</p>
          <p>By using {{ brandName }}' services, the User is deemed to have read, understood, and agreed to the terms of this Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">2. Personal Data Collected</h6>
          <p>{{ brandName }} may collect the following types of information:</p>
          <p><strong>Identity Information:</strong> Full name, identification number (ID/Passport), date of birth, nationality.</p>
          <p><strong>Contact Information:</strong> Address, phone number, and email address.</p>
          <p><strong>Transaction Information:</strong> Amount and destination of the transfer, recipient's name, bank account details.</p>
          <p><strong>Financial Information:</strong> Proof of source of funds, bank account used.</p>
          <p><strong>Technical Information:</strong> IP address, activity logs, device and application data.</p>
          <p><strong>Other Information</strong> required in accordance with laws and regulations on Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF).</p>
          
          <h6 class="fw-bold mt-4">3. Purpose of Data Collection</h6>
          <p>The personal data collected will be used for the following purposes:</p>
          <p>• To provide, manage, and improve {{ brandName }}' services.</p>
          <p>• To carry out identity verification and Know Your Customer (KYC) processes.</p>
          <p>• To comply with applicable laws and regulations (including AML-CTF).</p>
          <p>• To detect and prevent fraud, misuse, or other illegal activities.</p>
          <p>• To provide information to competent authorities when legally required.</p>
          <p>• To communicate important updates regarding transactions or services.</p>
          
          <h6 class="fw-bold mt-4">4. Legal Basis for Data Processing</h6>
          <p>We process personal data based on the following legal grounds:</p>
          <p>• The User's consent.</p>
          <p>• Legal obligations (including regulations from Bank Indonesia, PPATK, and other applicable laws).</p>
          <p>• Legitimate interests of the company in ensuring security and operational efficiency.</p>
          <p>• Contractual obligations between {{ brandName }} and the User.</p>
          
          <h6 class="fw-bold mt-4">5. Data Storage and Protection</h6>
          <p>• Data is stored on servers protected both physically and digitally, in accordance with industry standards.</p>
          <p>• {{ brandName }} implements encryption, firewalls, and access controls to prevent unauthorized access.</p>
          <p>• Data will be retained as long as necessary for processing purposes or as permitted by law.</p>
          
          <h6 class="fw-bold mt-4">6. Sharing of Personal Data</h6>
          <p>{{ brandName }} may share personal data with the following third parties:</p>
          <p>• Overseas correspondent partners for international remittance purposes.</p>
          <p>• Domestic correspondent partners for domestic fund transfers.</p>
          <p>• Verification and KYC service providers.</p>
          <p>• Regulatory and legal authorities (Bank Indonesia, PPATK, Police, etc.).</p>
          <p>All third parties are required to adhere to data protection principles equivalent to our own.</p>
          
          <h6 class="fw-bold mt-4">7. User Rights Regarding Personal Data</h6>
          <p>Users have the right to:</p>
          <p>• Access and obtain a copy of their personal data.</p>
          <p>• Update or correct inaccurate personal data.</p>
          <p>• Withdraw consent (with certain consequences on the services).</p>
          <p>• Request deletion of personal data, in accordance with applicable laws and regulations.</p>
          <p>Requests regarding these rights may be submitted via: <strong>privacy@swiftrans.co.id</strong></p>
          
          <h6 class="fw-bold mt-4">8. Cookies and Similar Technologies</h6>
          <p>We use cookies and similar technologies to:</p>
          <p>• Enhance user experience.</p>
          <p>• Collect data on service usage.</p>
          <p>• Provide more relevant content.</p>
          <p>Users can manage their cookie preferences through their respective browser settings.</p>
          
          <h6 class="fw-bold mt-4">9. Changes to the Privacy Policy</h6>
          <p>{{ brandName }} may update this Privacy Policy from time to time. We will notify Users of any material changes via the application, email, or other official communication channels.</p>
          <p>Continued use of the services following any changes will constitute acceptance of the updated Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">10. Contact Information</h6>
          <p>For any questions, information requests, or complaints regarding this Privacy Policy, please contact us at:</p>
          <p><strong>PT Anugrah Multi Remittance ({{ brandName }})</strong></p>
          <p><strong>Office Address:</strong> Komplek Bumi Indah Blok F No. 2, Lubuk Baja Kota, Lubuk Baja District, Batam City</p>
          <p><strong>Email:</strong> cs@swiftrans.co.id</p>
          
          <h6 class="fw-bold mt-4">11. Governing Law</h6>
          <p>This Policy shall be governed by and construed in accordance with the laws of the Republic of Indonesia, including Law Number 27 of 2022 on Personal Data Protection, and its implementing regulations.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showPrivacyModal = false">Close</button>
        </div>
      </div>
    </div>
  </div>

  <Loading :isLoading="isLoading" />
</template>

<script>
import { onBeforeUnmount, onBeforeMount } from "vue";
import { useStore } from "vuex";
import ArgonButton from "@/components/ArgonButton";
import { buildFormData } from "../../utils/param-builder";
import { loginMerchant } from "../../api/auth";
import Loading from "@/components/Loading";
import TermsAndConditionsModal from "@/components/TermsAndConditionsModal";
import { useBranding } from "@/utils/branding";

export default {
  components: {
    ArgonButton,
    Loading,
    TermsAndConditionsModal,
  },
  data() {
    const { brandLogo, brandName } = useBranding();
    return {
      email: "",
      password: "",
      showPassword: false,
      isLoading: false,
      loginError: "",
      emailError: "",
      passwordError: "",
      brandLogo,
      brandName,
      showTermsModal: false,
      showPrivacyModal: false,
    };
  },
  methods: {
    openTermsModal() {
      this.showTermsModal = true;
    },
    openPrivacyModal() {
      this.showPrivacyModal = true;
    },
    async submitForm() {
      // Clear previous errors
      this.emailError = "";
      this.passwordError = "";
      this.loginError = "";

      if (!this.validate()) {
        return;
      }

      this.isLoading = true;
      const body = {
        email: this.email,
        password: this.password,
      };

      try {
        const formData = buildFormData(body);
        const response = await loginMerchant(formData);
        if (response.status === "success") {
          this.$store.commit("auth/setEmail", response.data.merchant_email);
          localStorage.setItem("merchant_email", response.data.merchant_email);
          this.$router.push({ name: "VerifyOtp" });
        } else if (response.token) {
          await this.$store.dispatch("auth/setUserToken", response.data.token);
          this.$router.push("/home");
        } else {
          this.loginError = response.message || "Registration failed";
        }
      } catch (error) {
        this.loginError =
          error.response?.data.message || "Invalid email or password";
      } finally {
        this.isLoading = false;
      }
    },
    togglePasswordVisibility(field) {
      if (field === "password") {
        this.showPassword = !this.showPassword;
      }
    },
    validate() {
      let isValid = true;

      if (!this.email) {
        this.emailError = "Email is required";
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.emailError = "Please enter a valid email address";
          isValid = false;
        }
      }

      if (!this.password) {
        this.passwordError = "Password is required";
        isValid = false;
      }

      return isValid;
    },
    logout() {
      this.$store.dispatch("auth/clearToken");
      localStorage.removeItem("auth_token");
      this.$router.push({ name: "Signin" });
    },
  },
  setup() {
    const store = useStore();
    const body = document.getElementsByTagName("body")[0];

    onBeforeMount(() => {
      const storedEmail = localStorage.getItem("merchant_email");
      const storedToken = localStorage.getItem("auth_token");
      if (storedEmail) {
        store.commit("auth/setEmail", storedEmail);
      }
      if (storedToken) {
        store.commit("auth/setToken", storedToken);
      }
      store.state.hideConfigButton = true;
      store.state.showNavbar = false;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showNavbar = true;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    #ffffff 65%,
    #e8f5e8 65%,
    #d4f1d4 100%
  );
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40vh;
  background: linear-gradient(135deg, #e8f5e8 0%, #d4f1d4 50%, #c8f0c8 100%);
  z-index: 0;
}

/* Pattern Images */
.pattern-left {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.pattern-left img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.pattern-right {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
}

.pattern-right img {
  width: auto;
  height: 450px;
  object-fit: contain;
}

.container {
  position: relative;
  z-index: 1;
}

.card-custom {
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 2;
}

.shadow-lg {
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.text-label {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  margin-bottom: 0.5rem;
}

.text-title {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: #1f2937;
  text-align: center;
}

.custom-input {
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  transition: all 0.3s ease;
  font-size: 16px;
  background: #ffffff;
}

.custom-input:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
  outline: none;
}

.text-success-custom {
  color: #16a34a !important;
  font-weight: 600;
}

.forgot-password-link {
  font-size: 14px;
  transition: all 0.3s ease;
}

.forgot-password-link:hover {
  color: #15803d !important;
  text-decoration: underline !important;
}

.signup-link {
  font-weight: 600;
  transition: all 0.3s ease;
}

.signup-link:hover {
  color: #15803d !important;
  text-decoration: underline !important;
}

.custom-button {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem 2rem;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px 0 rgba(34, 197, 94, 0.3);
}

.custom-button:hover {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(34, 197, 94, 0.4);
}

.custom-button:active {
  transform: translateY(0);
}

.card-footer {
  border-radius: 0 0 1rem 1rem;
}

/* Animation for card entrance */
.card-custom {
  animation: slideUp 0.8s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .login-page::before {
    height: 30vh;
  }

  .pattern-left img,
  .pattern-right img {
    height: 400px;
  }

  .text-title {
    font-size: 24px;
    line-height: 32px;
  }

  .card-body {
    padding: 2rem !important;
  }
}

@media (max-width: 576px) {
  .pattern-left img,
  .pattern-right img {
    height: 150px;
  }
}

/* Override Bootstrap success color globally for this component */
.btn-success-custom {
  background-color: #22c55e;
  border-color: #22c55e;
}

.btn-success-custom:hover {
  background-color: #16a34a;
  border-color: #16a34a;
}

.btn-success-custom:focus,
.btn-success-custom.focus {
  box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
}

.btn-success-custom:disabled,
.btn-success-custom.disabled {
  background-color: #22c55e;
  border-color: #22c55e;
  opacity: 0.8;
}
</style>
